import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw, Search } from 'lucide-react';
import DataState from './DataState';
import LeaderboardFooter from './LeaderboardFooter';
import WalletLink from './WalletLink';

const apiUrl = process.env.REACT_APP_API_URL || '';

function AirdropPointsTable() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalItems: 0, totalPages: 0 });
  const [freshness, setFreshness] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchMessage, setSearchMessage] = useState('');

  const fetchRows = useCallback(async (signal) => {
    const params = new URLSearchParams({ metric: 'points', page: String(page), pageSize: String(pageSize) });
    if (activeSearch) params.set('wallet', activeSearch);

    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${apiUrl}/api/v2/leaderboard?${params}`, { signal });
      if (!response.ok) throw new Error('The rankings could not be loaded.');
      const data = await response.json();
      const nextRows = Array.isArray(data.data) ? data.data : [];
      setRows(nextRows);
      setPagination(data.pagination || { page: 1, pageSize, totalItems: 0, totalPages: 0 });
      setFreshness(data.freshness || null);
      if (activeSearch && nextRows.length === 0) setSearchMessage('No ranked holder was found for that wallet.');
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') {
        setError('The points service is unavailable right now. The last published rankings may still be syncing.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [activeSearch, page, pageSize]);

  useEffect(() => {
    const controller = new AbortController();
    fetchRows(controller.signal);
    return () => controller.abort();
  }, [fetchRows]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const walletId = searchValue.trim();
    setSearchMessage('');
    if (!walletId) {
      setSearchMessage('Enter a wallet address to search.');
      return;
    }
    setPage(1);
    setActiveSearch(walletId);
  };

  const handleResetResults = () => {
    setActiveSearch('');
    setSearchValue('');
    setSearchMessage('');
    setError('');
    setPage(1);
  };

  let state = null;
  if (loading && rows.length === 0) state = <DataState type="loading" title="Loading the leaderboard" message="Collecting the latest holder points." />;
  else if (error && rows.length === 0) state = <DataState type="error" title="Leaderboard unavailable" message={error} actionLabel="Retry" onAction={() => fetchRows()} />;
  else if (searchMessage) state = <DataState type="empty" title="No result yet" message={searchMessage} />;
  else if (rows.length === 0) state = <DataState type="empty" title="No rankings published" message="Leaderboard entries will appear after the next points update." />;

  return (
    <div className="data-tool">
      <form className="data-toolbar" onSubmit={handleSearchSubmit}>
        <label className="search-field">
          <span className="sr-only">Wallet address</span>
          <Search size={19} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search exact wallet address"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </label>
        <button className="button button-dark search-button" type="submit">Search</button>
        <button className="icon-button reset-button" type="button" onClick={handleResetResults} title="Reset results" aria-label="Reset results">
          <RotateCcw size={20} aria-hidden="true" />
        </button>
      </form>

      {error && rows.length > 0 && (
        <div className="inline-data-warning" role="alert">
          <span>Showing the last loaded page. The requested update failed.</span>
          <button type="button" onClick={() => fetchRows()}>Retry</button>
        </div>
      )}

      {state || (
        <>
          <div className="desktop-table-wrap">
            <table className="rankings-table">
              <thead>
                <tr><th>Rank</th><th>Wallet</th><th>Points</th><th>Daily earned</th><th>Adjustment</th></tr>
              </thead>
              <tbody>
                {rows.map((holder) => (
                  <tr key={holder.walletId}>
                    <th scope="row"><span className="rank-badge">{holder.rank}</span></th>
                    <td><WalletLink address={holder.walletId} /></td>
                    <td className="number-cell">{Number(holder.totalPoints || 0).toLocaleString()}</td>
                    <td><span className="change-badge">+{Number(holder.dailyEarned || 0).toLocaleString()}</span></td>
                    <td><span className={`change-badge ${Number(holder.transactionAdjustment) < 0 ? 'is-negative' : ''}`}>{Number(holder.transactionAdjustment) > 0 ? '+' : ''}{Number(holder.transactionAdjustment || 0).toLocaleString()}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mobile-ranking-list">
            {rows.map((holder) => (
              <article className="mobile-ranking-row mobile-points-row" key={holder.walletId}>
                <div className="mobile-rank">{holder.rank}</div>
                <div className="mobile-wallet"><span>Wallet</span><WalletLink address={holder.walletId} /></div>
                <div className="mobile-metric"><span>Points</span><strong>{Number(holder.totalPoints || 0).toLocaleString()}</strong></div>
                <div className="mobile-metric"><span>Daily</span><strong>+{Number(holder.dailyEarned || 0).toLocaleString()}</strong></div>
                <div className="mobile-adjustment"><span>Adjustment</span><strong>{Number(holder.transactionAdjustment) > 0 ? '+' : ''}{Number(holder.transactionAdjustment || 0).toLocaleString()}</strong></div>
              </article>
            ))}
          </div>
        </>
      )}

      {!state && !activeSearch && (
        <LeaderboardFooter
          freshness={freshness}
          pagination={pagination}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(nextSize) => { setPageSize(nextSize); setPage(1); }}
          disabled={loading}
        />
      )}
      {!state && activeSearch && (
        <div className="search-rank-summary">Global rank #{rows[0]?.rank?.toLocaleString()} · {Number(rows[0]?.percentile || 0).toFixed(2)}th percentile</div>
      )}
    </div>
  );
}

export default AirdropPointsTable;
